import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchIssues, fetchOverview, fetchSubscribers, fetchSyncStatus } from "../lib/api";
import { fetchAnalytics, rate } from "../lib/api-broadcasts";
import { HomeHero } from "../components/HomeHero";
import { useIssueStore } from "../lib/issue-store";
import { roleCan, useRole } from "../lib/roles";
import { countryLabel } from "../lib/countries";
import { COUNTRY_COLORS } from "../lib/demo-data";
import { ammanStamp, relativeTime } from "../lib/dates";
import { BarList } from "../components/BarList";
import { DataTable } from "../components/DataTable";
import { PageSkeleton } from "../components/PageSkeleton";
import { DonutChart } from "../components/Sparkline";
import { Bidi, EmptyState, SectionHeader, Surface } from "../components/primitives";

const nf = new Intl.NumberFormat("en-US");

/**
 * Home: the hero, then real numbers, then who just joined.
 *
 * It used to add a four-step explainer, a "Full loop" line and seven shortcut
 * tiles that repeated the sidebar, and its stat cards carried invented trend
 * deltas. Every figure here is now counted; where nothing has happened yet
 * (no newsletter sent) it says so.
 */
export function OverviewPage() {
  const overview = useQuery({ queryKey: ["overview"], queryFn: fetchOverview });
  const sync = useQuery({ queryKey: ["sync"], queryFn: fetchSyncStatus });
  const subs = useQuery({ queryKey: ["subscribers", ""], queryFn: () => fetchSubscribers("", 5) });
  const sends = useQuery({ queryKey: ["analytics", 365], queryFn: () => fetchAnalytics(365) });
  const issuesSeed = useQuery({ queryKey: ["issues"], queryFn: fetchIssues });
  const store = useIssueStore(issuesSeed.isError ? [] : issuesSeed.data);
  const { role } = useRole();
  const navigate = useNavigate();

  const stats = overview.data;
  const last = sends.data?.broadcasts.find((b) => b.sent > 0) ?? null;

  const countryBars = useMemo(() => (stats?.countries_top ?? []).map((c, i) => ({
    id: c.country,
    label: countryLabel(c.country),
    value: c.count,
    displayValue: c.count,
    format: (v: number) => nf.format(v),
    meta: `${c.share}%`,
    color: COUNTRY_COLORS[i % COUNTRY_COLORS.length],
  })), [stats]);

  const subCols = useMemo<ColumnDef<{ id: string; email: string | null; country: string | null }>[]>(() => [
    { accessorKey: "email", header: "Email", cell: (c) => <Bidi>{c.getValue<string>() ?? "—"}</Bidi> },
    { accessorKey: "country", header: "Country", cell: (c) => countryLabel(c.getValue<string>()) },
  ], []);

  if (overview.isLoading) return <PageSkeleton />;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-5)" }}>
      {/* The hero holds the page title, so there is no page header above it. */}
      <HomeHero
        issues={store.issues ?? []}
        subscribers={stats ? stats.total_subscribers : null}
        canCreate={roleCan(role, "newsletters.write")}
        onCreate={() => {
          const draft = store.create({ title: "", subject: "" });
          navigate(`/content/${draft.id}`, { state: { issue: draft } });
        }}
      />

      {overview.isError ? (
        <EmptyState title="Numbers could not be loaded" body={(overview.error as Error).message} hint="Reload the page to try again." />
      ) : stats && (
        <>
          <dl className="bc-stats ov-stats">
            <div className="bc-stat">
              <dt>Subscribers</dt>
              <dd className="tabnum">{nf.format(stats.total_subscribers)}</dd>
              <dd className="bc-stat-note">On the newsletter list</dd>
            </div>
            <div className="bc-stat">
              <dt>Markets</dt>
              <dd className="tabnum">{nf.format(stats.countries_top.length)}</dd>
              <dd className="bc-stat-note">Countries they're in</dd>
            </div>
            <div className="bc-stat">
              <dt>Last newsletter</dt>
              {last ? (
                <>
                  <dd className="tabnum">{rate(last.opened, last.sent)}</dd>
                  <dd className="bc-stat-note">opened · <Link to={`/broadcasts/${last.id}`}>{ammanStamp(last.sent_at)}</Link></dd>
                </>
              ) : (
                <>
                  <dd className="tabnum">—</dd>
                  <dd className="bc-stat-note">None sent yet</dd>
                </>
              )}
            </div>
            <div className="bc-stat">
              <dt>List last synced</dt>
              <dd className="tabnum ov-stat-date">{sync.data?.last_success ? relativeTime(sync.data.last_success) : "—"}</dd>
              <dd className="bc-stat-note">{sync.data?.last_success ? ammanStamp(sync.data.last_success) : "Not reported"}</dd>
            </div>
          </dl>

          {stats.countries_top.length > 0 && (
            <Surface style={{ overflow: "hidden" }}>
              <div className="nl-pane-grid">
                <div className="nl-pane">
                  <SectionHeader compact title="By country" meta="Where subscribers are" />
                  <div style={{ padding: "var(--ui-space-2) var(--ui-space-4) var(--ui-space-4)" }}>
                    <DonutChart
                      slices={stats.countries_top.map((c, i) => ({
                        id: c.country || `c-${i}`,
                        label: countryLabel(c.country) || c.country || "Unknown",
                        value: c.count,
                        color: COUNTRY_COLORS[i % COUNTRY_COLORS.length],
                      }))}
                      center={nf.format(stats.total_subscribers)}
                      sublabel="Subscribers"
                    />
                  </div>
                </div>
                <div className="nl-pane">
                  <SectionHeader compact title="Top markets" meta="Share of subscribers" />
                  <div style={{ padding: "0 var(--ui-space-4) var(--ui-space-4)" }}>
                    <BarList rows={countryBars} />
                  </div>
                </div>
              </div>
            </Surface>
          )}
        </>
      )}

      <Surface>
        <SectionHeader title="Recent subscribers" meta="The latest sign-ups" />
        <div style={{ padding: "0 var(--ui-space-4) var(--ui-space-4)" }}>
          {subs.isLoading ? (
            <p className="bc-loading">Loading…</p>
          ) : subs.isError ? (
            <p className="bc-hint">Subscribers could not be loaded.</p>
          ) : (
            <DataTable data={subs.data?.rows ?? []} columns={subCols} paginate={false} ariaLabel="Recent subscribers" />
          )}
        </div>
      </Surface>
    </div>
  );
}
